import { createWebHistory, createRouter, RouteRecordRaw } from "vue-router";
import { useApp } from "../../pages-vue/App/useApp";

import { PermissionManager } from "../utilities/permission-manager";
import { User } from "../../models/user";
import ReportsRouter from "../../pages-vue/Reports/ReportsRouter";
import PatientsRouter from "../../pages-vue/Patients/PatientsRouter";
import CentralIntakeRouter from "../../pages-vue/CentralIntake/CentralIntakeRouter";
import CentralCalendarRouter from "../../pages-vue/CentralCalendar/CentralCalendarRouter";
import CentralRouter from "../../pages-vue/AxxessCentral/CentralRouter";
import AdminRouter from "../../pages-vue/App/Admin/AdminRouter";
import AxxessHomeRouter from "../../pages-vue/AxxessHome/AxxessHomeRouter";
import axxessMessagingLandingPage from "../../pages-vue/MessageCenter/axxess-messaging-landing-page-routes";

import PersonalCenter from "../../pages-vue/Personal/PersonalCenter.vue";
import CentralCalendar from "../../pages-vue/CentralCalendar/CentralCalendar.vue";
import Admin from "../../pages-vue/App/Admin/Admin.vue";
import CreateInterimReferral from "../../pages-vue/CreateInterimReferral/CreateInterimReferral.vue";
import CreateReferralHosPal from "../../pages-vue/CreateReferral/CreateReferralHosPal/CreateReferralHosPal.vue";
import CreateReferralHomeHealth from "../../pages-vue/CreateReferral/CreateReferralHomeHealth/CreateReferralHomeHealth.vue";
import CreateReferralHomeCare from "../../pages-vue/CreateReferral/CreateReferralHomeCare/CreateReferralHomeCare.vue";
import EditReferralHomeHealth from "../../pages-vue/EditReferralHomeHealth/EditReferralHomeHealth.vue";
import AxxessCentral from "../../pages-vue/AxxessCentral/CentralApp.vue";
import taskManager from "../../pages-vue/TaskManagerEmployee/TaskManager.vue";
import taskManagerPatient from "../../pages-vue/TaskManagerPatient/TaskManager.vue";
import SelectOrganization from "../../pages-vue/SelectOrganization/SelectOrganization.vue";
import CentralIntake from "../../pages-vue/CentralIntake/CentralIntake.vue";
import AxxessHome from "../../pages-vue/AxxessHome/AxxessHome.vue";
import ConvertToPendingPal from "../../pages-vue/CreateReferral/ConvertToPendingPal/ConvertToPendingPal.vue";
import HospiceConvertToPending from "../../pages-vue/CreateReferral/HospiceConvertToPending/HospiceConvertToPending.vue";
import Reports from "../../pages-vue/Reports/Reports.vue";
import FindNearByEmployeesForReferral from "../../pages-vue/CentralIntake/FindNearByEmployeesForReferral/FindNearByEmployeesForReferral.vue";
import CentralMessaging from "../../pages-vue/MessageCenter/CentralMessaging/CentralMessaging.vue";
import UnassignedPatientVisitsMap from "../../pages-vue/CentralCalendar/UnassignedPatientVisits/UnassignedPatientVisitsMap/UnassignedPatientVisitsMap.vue";
import Patients from "../../pages-vue/Patients/Patients.vue";

export enum RouteNames {
    Home = "home",
    Help = "help",
    Clinician = "clinician",
    Calendar = "calendar",
    Admin = "admin",
    OidcSignIn = "oidc-sign-in",
    OidcSignInRedirect = "oidc-sign-in-redirect",
    OidcSignInSilent = "oidc-sign-in-silent",
    Intake = "intake",
    Create = "create",
    Patients = "patients",
    Central = "central",
    TaskManager = "task-manager",
    TaskManagerPatient = "task-manager-patient",
    ConvertToPending = "convert-to-pending",
    PalConvertToPending = "pal-convert-to-pending",
    Reports = "reports",
    FindNearByEmployees = "find-nearby-employees"
}

const title: string = "Axxess - Complete Home Health Care Software";

const routes: Array<RouteRecordRaw> = [
    {
        path: "/",
        component: AxxessHome,
        meta: {
            nav: false
        },
        beforeEnter: async () => {
            // router guard to protect unauthorized users from accessing connect through URL
            const appStore = useApp();
            await appStore.loadRoles();

            const connectPromise = appStore.loadConnectPermission();

            // if connect permission api takes more than 5 secs -> start loading other apis
            const result = await Promise.race([
                connectPromise.then(() => "connectPermission"),
                new Promise((resolve) => setTimeout(resolve, 5000)).then(() => "timeout")
            ]);

            if (result === "connectPermission") {
                if (appStore.hasAxxessConnectPermission || appStore.isAnyPremiumAgency) {
                    return "/central-messaging/clinicians/conversation";
                }
                if (appStore.isClinician) {
                    return "/clinician";
                }
                return "/help";
            }

            if (appStore.isClinician) {
                return "/clinician";
            }
            return "/help";
        }
    },
    {
        path: "/clinician",
        name: RouteNames.Clinician,
        component: AxxessHome,
        meta: {
            title: title,
            nav: true,
            menuName: "Axxess Home",
            showAxxessPlanner: true,
            requiresPlannerPermission: true,
            navItem: "tabClinician"
        },
        children: AxxessHomeRouter
    },
    {
        path: "/axxess-central",
        name: RouteNames.Central,
        redirect: "/axxess-central/dashboard",
        meta: {
            title: title,
            nav: true,
            menuName: "Axxess Central",
            centralNav: false,
            showAxxessCentral: true
        }
    },

    {
        path: "/axxess-central/dashboard",
        name: "Home",
        component: AxxessCentral,
        meta: {
            title: "Home",
            nav: false,
            menuName: "Home",
            centralNav: true,
            iconClass: "fas fa-home",
            navItem: "tabHome",
            showAxxessCentral: true
        },
        children: CentralRouter
    },
    {
        path: "/axxess-central/intake",
        name: RouteNames.Intake,
        component: CentralIntake,
        redirect: "/axxess-central/intake/inquiry-list",
        meta: {
            nav: false,
            title: title,
            menuName: "Central Intake",
            showCentralIntake: true,
            hideTitle: true,
            centralNav: true,
            iconClass: "fa fa-user-plus",
            showAxxessCentral: true
        },
        children: CentralIntakeRouter
    },
    {
        path: "/axxess-central/referral/find-nearby-employees",
        name: RouteNames.FindNearByEmployees,
        component: FindNearByEmployeesForReferral,
        meta: {
            showAxxessCentral: true,
            nav: false
        }
    },
    {
        path: "/axxess-central/create",
        name: RouteNames.Create,
        children: [
            {
                path: "/axxess-central/create/interim/referral",
                component: CreateInterimReferral,
                name: "create-interim-referral"
            },

            {
                path: "/axxess-central/create/referral",
                component: CreateReferralHosPal,
                name: "hos-pal-referral"
            },

            {
                path: "/axxess-central/create/homehealth-referral",
                component: CreateReferralHomeHealth,
                name: "homehealth-referral"
            },
            {
                path: "/axxess-central/create/edit-home-health-referral/:id?/accountId/:accountId?/application/:applicationId?/convertToPending/:convertToPending?",
                component: EditReferralHomeHealth,
                name: "edit-home-health-referral"
            },
            {
                path: "/axxess-central/create/homecare-referral",
                component: CreateReferralHomeCare,
                name: "homecare-referral"
            }
        ],
        meta: {
            showAxxessCentral: true,
            nav: false
        }
    },

    {
        path: "/axxess-central/patients/patient-edit",
        name: RouteNames.ConvertToPending,
        component: HospiceConvertToPending,
        meta: {
            showAxxessCentral: true,
            nav: false
        }
    },
    {
        path: "/axxess-central/patients/patient-edit-pal/:patientId/accountId/:accountId/patientReferralId/:patientReferralId?",
        name: RouteNames.PalConvertToPending,
        component: ConvertToPendingPal,
        meta: {
            showAxxessCentral: true,
            nav: false
        }
    },
    // Central Calendar was renamed to Schedule Center
    {
        path: "/axxess-central/schedule-center",
        name: RouteNames.Calendar,
        component: CentralCalendar,
        meta: {
            nav: false,
            title: title,
            menuName: "Schedule Center",
            showIfScheduler: true,
            centralNav: true,
            iconClass: "far fa-calendar-alt",
            showAxxessCentral: true
        },
        children: CentralCalendarRouter
    },
    {
        path: "/axxess-central/patients",
        name: RouteNames.Patients,
        component: Patients,
        meta: {
            nav: false,
            title: "Patients",
            showIfScheduler: true,
            menuName: "Patients",
            centralNav: true,
            iconClass: "fa fa-user-plus",
            showAxxessCentral: true
        },
        children: PatientsRouter
    },
    {
        path: "/axxess-central/Admin",
        name: RouteNames.Admin,
        component: Admin,
        meta: {
            nav: false,
            title: title,
            menuName: "Admin",
            showIfAdmin: true,
            centralNav: true,
            showAxxessCentral: true
        },
        children: AdminRouter
    },
    {
        path: "/axxess-central/schedule-center/unassigned-patient-visits/map",
        component: UnassignedPatientVisitsMap,
        name: "unassigned patient visits map",
        meta: {
            showAxxessCentral: true,
            nav: false,
            showIfNotOnlyHH: true
        }
    },
    {
        path: "/axxess-central/reports",
        name: RouteNames.Reports,
        component: Reports,
        meta: {
            nav: false,
            title: title,
            menuName: "Reports",
            showIfScheduler: true,
            centralNav: true,
            iconClass: "far fa-calendar-alt",
            showAxxessCentral: true
        },
        children: ReportsRouter
    },
    {
        path: "/axxess-central/task-manager",
        name: RouteNames.TaskManager,
        component: taskManager,
        meta: {
            nav: false,
            title: title,
            menuName: "Task Manager",
            showIfScheduler: true,
            showAxxessCentral: true
        }
    },
    {
        path: "/axxess-central/task-manager-patient",
        name: RouteNames.TaskManagerPatient,
        component: taskManagerPatient,
        meta: {
            nav: false,
            title: title,
            menuName: "Task Manager",
            showIfScheduler: true,
            showAxxessCentral: true
        }
    },
    {
        path: "/help",
        name: RouteNames.Help,
        component: PersonalCenter,
        meta: {
            nav: false,
            title: title,
            menuName: "Axxess Help Center",
            showOther: true,
            navItem: "tabHelp"
        }
    },
    {
        path: "/axxess-central/organizationLoading",
        component: SelectOrganization,
        name: "Organization Loading",
        meta: {
            menuName: "",
            navItem: "tabLoading",
            showAxxessCentral: true
        }
    },
    {
        path: "/central-messaging/clinicians/conversation",
        component: CentralMessaging,
        name: "Axxess Connect",
        meta: {
            menuName: "Axxess Connect",
            navItem: "tabMessaging",
            showAxxessConnect: true,
            nav: false,
            requiresConnectPermission: true
        },
        children: axxessMessagingLandingPage
    },
    {
        path: "/:pathMatch(.*)*",
        redirect: "/central-messaging/clinicians/conversation"
    }
];

export function checkPermission(permissionManager: PermissionManager, user: User) {
    if (!permissionManager || !user) {
        return null;
    }
    // We might use the below commented logic in future to restrict permission based urls
    // const permissionsToViewPaths: { [key: string]: boolean } = {
    //     clinician: permissionManager.checkHasAnyPermission(ParentPermissionEnum.clinician)
    // };
    // const pathName = route.meta. as string;
    // if (pathName in permissionsToViewPaths) {
    //     return permissionsToViewPaths[pathName];
    // }

    const appStore = useApp();
    return (route: RouteRecordRaw) => {
        if (route?.meta?.showAxxessCentral) {
            return appStore.organizations?.length > 0;
        }
        return true;
    };
}

const VueRouter = createRouter({
    history: createWebHistory(),
    routes: routes
});

VueRouter.beforeEach(async (to) => {
    // router guard to protect unauthorized users from accessing connect through URL
    const appStore = useApp();

    if (to.meta.requiresConnectPermission) {
        await appStore.loadConnectPermission();

        if (!appStore.hasAxxessConnectPermission || !appStore.isAnyPremiumAgency) {
            await appStore.loadRoles();

            if (appStore.isClinician) {
                return "/clinician";
            }
            return "/help";
        }
        return true;
    }
    if (to.meta.requiresPlannerPermission) {
        await appStore.loadRoles();

        if (!appStore.isClinician) {
            return "/help";
        }

        return true;
    }
    return true;
});

export default VueRouter;
